// De Agenda (tab 2 van de Intelligence-pagina): per inzicht-cel hoe ver hij nog
// van publiceerbaar af staat, dichtstbij eerst.
//
// Dit is geen tweede rekenmodel. De afstand komt uit getInsightNextTierGaps en
// de drempels uit P2_TARGETS — één bron, gespiegeld op
// functions/insightStrength/parameters.js.
//
// Rangschikken gebeurt op de BINDENDE kolom, niet op het gunstigste getal: P2
// neemt het minimum over zijn kolommen, dus een cel met 240 respondenten bij 2
// organisaties staat verder van de poort dan een cel met 90 respondenten bij 8.
// Sorteren op respondenten zou de eerste bovenaan zetten en daarmee een
// verkeerd verkoopdoel aanwijzen.
//
// AI-CONTEXT: See docs/features/intelligence-research-questions.md

import getInsightNextTierGaps, { P2_TARGETS } from '../sectorInsights/getInsightNextTierGaps'
import segmentLabel from './segmentLabel'
import cellPublishBlocker from './cellPublishBlocker'

/** @param {*} value @returns {number} */
const num = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : 0)

/**
 * @typedef {object} AgendaRow
 * @property {string} cellId - Stabiele celsleutel
 * @property {string} msiId - Effect waar de cel bij hoort
 * @property {string} effectName - Naam van dat effect ('' als het celdoc hem nog niet draagt)
 * @property {Array<string>} sectors - Sectoren van het effect (leeg als onbekend)
 * @property {string} dimension - activity | mechanismCategory | mechanismSignal | targetgroup | context
 * @property {string} dimensionLabel - Leesbaar label van de celwaarde
 * @property {number} nOrgs - Bijdragende organisaties nu
 * @property {number} nRespondents - Respondenten nu
 * @property {number} needsOrgs - Organisaties tekort tot de poort (0 als hij gehaald is)
 * @property {number} needsRespondents - Respondenten tekort tot de poort (0 als hij gehaald is)
 * @property {number} readiness - 0–1, de laagste van beide kolommen; 1 = poort gehaald
 * @property {number|null} maxOrgShare - Aandeel van de grootste organisatie (0–1), of null
 * @property {{orgId: string, name: string, respondents: number}|null} topContributor - Grootste bijdrager, of null
 * @property {string|null} segment - Doelgroep waar deze cel op inzoomt; null = ongesegmenteerd
 * @property {string} segmentLabel - Leesbare naam van die doelgroep ('' als ongesegmenteerd)
 * @property {boolean} publishable - P2 gehaald én een toonbare classificatie (zoals de server publiceren toestaat)
 * @property {('p2'|'classificatie'|null)} blockedBy - Waarom niet publiceerbaar; null als het wel kan
 * @property {object|null} cell - Het celdocument zelf, voor de onderbouwing achter de regel
 */

/**
 * Cellen omzetten naar agendaregels, dichtst bij de poort eerst.
 *
 * @param {Array<object>} [cells] - Cellen zoals getInsightCells ze levert
 * @param {{orgs?: number, respondents?: number}} [targets] - P2-doelen uit dezelfde respons
 * @returns {AgendaRow[]}
 */
const buildAgendaRows = (cells, targets = null) => {
    if (!Array.isArray(cells)) return []

    // De server stuurt de drempels mee; P2_TARGETS is de terugval.
    const doelOrgs = (targets && typeof targets.orgs === 'number') ? targets.orgs : P2_TARGETS.orgs
    const doelResp = (targets && typeof targets.respondents === 'number')
        ? targets.respondents
        : P2_TARGETS.respondents

    return cells
        .map((cell) => {
            const source = cell || {}
            const gaps = getInsightNextTierGaps(source, targets)
            const nOrgs = num(source.nOrgs)
            const nRespondents = num(source.nRespondents)
            // Server-waarheid: het celdocument draagt sinds ISS 2026.8 `passed`.
            // Een oud document zonder dat veld is niet publiceerbaar — de
            // versie-filter houdt die toch al buiten de agenda.
            // AI-CONTEXT: See docs/pitfalls.md#dev-mirrored-constant-guarded-only-by-a-comment
            // Publiceerbaar = wat de server afdwingt (ADR-057): P2 gehaald ÉN een
            // toonbare classificatie. P2 alleen zette 'klaar om te publiceren' bij
            // cellen die de server weigert. De reden gaat mee, zodat de regel niet
            // 'de spreiding houdt de poort tegen' zegt waar de classificatie het is.
            const blockedBy = cellPublishBlocker(source)
            const publishable = blockedBy === null

            // Een segmentcel telt alleen de respondenten die via hun onderzoek
            // aan deze doelgroep hangen. Zonder markering staan er twee regels
            // voor hetzelfde effect en dezelfde dimensie met verschillende
            // getallen, wat als rekenfout leest in plaats van als twee
            // verschillende uitspraken. Een onbekende sleutel valt terug op de
            // sleutel zelf: leeg zou de regel als ongesegmenteerd laten lezen.
            const segment = typeof source.segment === 'string' && source.segment ? source.segment : null

            return {
                // De onderbouwing (classificatie, design, B, E, meetronden,
                // replicatie) staat in het celdocument, niet in de regel. De
                // agenda heeft alle cellen al in handen, dus die reist mee:
                // per uitklap opnieuw ophalen zou dezelfde gegevens nog een
                // keer over de lijn halen, per regel apart.
                cell: cell || null,
                segment,
                segmentLabel: segmentLabel(segment),
                cellId: source.cellId || '',
                msiId: source.msiId || '',
                effectName: source.effectName || '',
                sectors: Array.isArray(source.sectors) ? source.sectors : [],
                // Dominantie hoort mee de lijst in: een cel waarvan één
                // organisatie het leeuwendeel levert, is geen aggregaat.
                maxOrgShare: typeof source.gates?.p2?.maxOrgShare === 'number'
                    ? source.gates.p2.maxOrgShare
                    : null,
                topContributor: Array.isArray(source.contributors) && source.contributors.length
                    ? source.contributors[0]
                    : null,
                dimension: source.dimension || '',
                dimensionLabel: source.dimensionLabel || '',
                nOrgs,
                nRespondents,
                // null uit de gap-helper betekent "niets meer nodig op deze kolom"
                needsOrgs: gaps.nOrgs || 0,
                needsRespondents: gaps.nRespondents || 0,
                readiness: publishable
                    ? 1
                    : Math.min(1, nOrgs / doelOrgs, nRespondents / doelResp),
                publishable,
                blockedBy,
            }
        })
        // Dichtstbij eerst; bij gelijke stand de breedst gedragen cel eerst,
        // daarna op sleutel zodat de volgorde tussen runs niet verspringt.
        .sort((a, b) =>
            b.readiness - a.readiness
            || b.nOrgs - a.nOrgs
            || a.cellId.localeCompare(b.cellId))
}

export default buildAgendaRows
